import React, { useEffect, useState } from "react";
import { Download, CheckCircle2, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true));

const isIOS = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ si presenta come Mac ma con touch
  const iPadOS = /Macintosh/i.test(ua) && (window.navigator.maxTouchPoints || 0) > 1;
  return iOSDevice || iPadOS;
};

export const InstallAppButton = ({ className = "" }) => {
  const [installed, setInstalled] = useState(isStandalone());
  const [canPrompt, setCanPrompt] = useState(!!(typeof window !== "undefined" && window.__deferredInstallPrompt));
  const [help, setHelp] = useState(null); // "ios" | "generic" | null
  const ios = isIOS();

  useEffect(() => {
    const onAvail = () => setCanPrompt(true);
    const onInstalled = () => { setInstalled(true); setCanPrompt(false); setHelp(null); };
    window.addEventListener("lsh-install-available", onAvail);
    window.addEventListener("lsh-app-installed", onInstalled);
    const mq = window.matchMedia?.("(display-mode: standalone)");
    const onMq = () => setInstalled(isStandalone());
    mq?.addEventListener?.("change", onMq);
    return () => {
      window.removeEventListener("lsh-install-available", onAvail);
      window.removeEventListener("lsh-app-installed", onInstalled);
      mq?.removeEventListener?.("change", onMq);
    };
  }, []);

  const handleClick = async () => {
    const dp = window.__deferredInstallPrompt;
    if (dp && !ios) {
      try {
        dp.prompt();
        const choice = await dp.userChoice;
        if (choice?.outcome === "accepted") setInstalled(true);
        window.__deferredInstallPrompt = null;
        setCanPrompt(false);
      } catch {
        setHelp("generic");
      }
      return;
    }
    setHelp(ios ? "ios" : "generic");
  };

  if (installed) {
    return (
      <span data-testid="install-app-installed"
        className={`inline-flex items-center gap-2 rounded-full h-12 px-6 bg-white/10 border border-white/25 text-white text-base ${className}`}>
        <CheckCircle2 className="h-5 w-5 text-emerald-400" /> App installata
      </span>
    );
  }

  return (
    <>
      <Button data-testid="install-app-btn" onClick={handleClick}
        className={`rounded-full h-12 px-7 bg-brand-orange hover:bg-brand-orange/90 text-white text-base gap-2 hover:-translate-y-0.5 transition-transform ${className}`}>
        <Download className="h-5 w-5" /> Installa l'app
      </Button>

      <Dialog open={!!help} onOpenChange={(o) => !o && setHelp(null)}>
        <DialogContent className="max-w-sm" data-testid="install-app-dialog">
          <DialogHeader>
            <DialogTitle>Installa LearnSphereHub</DialogTitle>
            <DialogDescription>Aggiungi l'app alla schermata Home del tuo dispositivo.</DialogDescription>
          </DialogHeader>
          {help === "ios" ? (
            <div data-testid="install-app-ios-steps" className="text-brand-navy text-base space-y-3">
              <p className="flex items-center gap-2"><span className="font-bold">1.</span> Tocca <Share className="h-4 w-4 inline" /> <b>Condividi</b></p>
              <p className="flex items-center gap-2"><span className="font-bold">2.</span> Seleziona <b>Aggiungi alla schermata Home</b></p>
            </div>
          ) : (
            <div data-testid="install-app-generic-steps" className="text-brand-navy text-base space-y-2">
              <p>Apri il menu del browser e scegli <b>“Installa app”</b> oppure <b>“Aggiungi alla schermata Home”</b>.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallAppButton;
